import { useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
} from "@pushpress/pushpress-ui";
import { Icons } from "../../icons";
import { cn } from "../../utils/css";
import { useNavigate } from "../../hooks/useNavigate";

export type BaseNudgeProps = {
  nudge: {
    key: string;
    title?: string;
    description?: string;
    ctaType?: "link" | "callback";
    ctaAction?: string;
    ctaText?: string;
  };
  onClick?: () => void;
  onDismiss?: () => void;
  trackingRef?: (element: HTMLDivElement | null) => void;
};

export type NudgeVariant = "default" | "info" | "warning" | "success";

export type NudgeProps = BaseNudgeProps & {
  variant?: NudgeVariant;
  icon?: keyof typeof Icons | null | true;
  dismissible?: boolean;
};

const variantStyles = {
  default: {
    container:
      "tw-bg-neutral-100 tw-border-neutral-200 tw-outline-neutral-200 tw-outline-1",
    icon: "tw-text-neutral-500 tw-bg-neutral-100 tw-rounded-full tw-p-1.5 tw-outline tw-outline-neutral-200 tw-border tw-border-neutral-300",
    title: "tw-text-neutral-600",
    description: "tw-text-neutral-500",
    dismissButton:
      "tw-text-neutral-500 hover:tw-text-neutral-700 hover:tw-bg-neutral-100",
  },
  info: {
    container:
      "tw-bg-info-light tw-border-info-medium tw-outline tw-outline-1 tw-outline-info-medium/50",
    icon: "tw-text-info tw-bg-info-100 tw-rounded-full tw-p-1.5 tw-outline tw-outline-info-200 tw-border tw-border-info-300",
    title: "tw-text-info-dark",
    description: "tw-text-info-dark/80",
    dismissButton:
      "tw-text-info-dark hover:tw-text-info-dark hover:tw-bg-info-light",
  },
  warning: {
    container:
      "tw-bg-warning-light tw-border-warning-medium tw-outline tw-outline-1 tw-outline-warning-medium/50",
    icon: "tw-text-warning tw-bg-warning-100 tw-rounded-full tw-p-1.5 tw-outline tw-outline-warning-200 tw-border tw-border-warning-300",
    title: "tw-text-warning-dark",
    description: "tw-text-warning-dark/80",
    dismissButton:
      "tw-text-warning-dark hover:tw-text-warning-dark hover:tw-bg-warning-light",
  },
  success: {
    container:
      "tw-bg-success-light tw-border-success-medium tw-outline tw-outline-1 tw-outline-success-medium/50",
    icon: "tw-text-success tw-bg-success-100 tw-rounded-full tw-p-1.5 tw-outline tw-outline-success-200 tw-border tw-border-success-300",
    title: "tw-text-success-dark",
    description: "tw-text-success-dark/80",
    dismissButton:
      "tw-text-success-dark hover:tw-text-success-dark hover:tw-bg-success-light",
  },
};

export function Nudge({
  nudge,
  onClick,
  onDismiss,
  variant = "default",
  icon,
  dismissible = true,
  trackingRef,
}: NudgeProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();

  const handleDismiss = async () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleClick = async () => {
    if (onClick) {
      onClick();
    }

    if (nudge.ctaType === "link") {
      if (nudge.ctaAction?.startsWith("http")) {
        window.open(nudge.ctaAction, "_blank");
      } else {
        navigate(nudge.ctaAction);
      }
    }
  };

  if (!nudge || isDismissed) {
    return null;
  }

  const styles = variantStyles[variant];
  const IconComponent = icon
    ? icon === true
      ? Icons.Default
      : Icons[icon]
    : null;

  return (
    <div ref={trackingRef}>
      <Alert
        variant="default"
        className={cn(
          styles.container,
          "tw-flex tw-items-center tw-gap-3 tw-py-2 tw-relative tw-transition-all tw-duration-200",
        )}
      >
        <div className="tw-flex tw-items-center tw-gap-x-3 tw-w-full">
          <div>
            {IconComponent ? (
              <IconComponent
                className={cn(
                  "tw-h-8 tw-w-8 tw-flex-shrink-0 tw-shrink-0",
                  styles.icon,
                )}
              />
            ) : (
              <div className="tw-w-2 tw-h-2 tw-bg-neutral-300 tw-rounded-full tw-outline tw-outline-neutral-200 tw-flex-shrink-0 tw-shrink-0" />
            )}
          </div>

          <div className="tw-flex tw-justify-between tw-w-full tw-items-center tw-min-w-0">
            <div className="tw-flex tw-flex-col tw-pr-4 tw-min-w-0 tw-flex-1">
              <AlertTitle
                className={cn(
                  styles.title,
                  "tw-font-semibold tw-text-sm tw-leading-tight",
                )}
              >
                {nudge.title}
              </AlertTitle>
              <AlertDescription
                className={cn(
                  styles.description,
                  "tw-text-sm tw-leading-relaxed -tw-mt-1",
                )}
              >
                {nudge.description}
              </AlertDescription>
            </div>

            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
              {nudge.ctaText && onClick && (
                <Button
                  variant="outline"
                  size="sm"
                  className="tw-text-xs tw-px-3 tw-py-1.5 tw-h-auto tw-border-current tw-transition-colors"
                  onClick={handleClick}
                >
                  {nudge.ctaText}
                </Button>
              )}

              {dismissible && onDismiss && (
                <button
                  type="button"
                  onClick={handleDismiss}
                  className={cn(
                    styles.dismissButton,
                    "tw-bg-transparent tw-border tw-border-transparent tw-cursor-pointer tw-rounded-full tw-p-1.5 tw-transition-colors tw-flex tw-items-center tw-justify-center",
                    "hover:tw-border-neutral",
                  )}
                  aria-label="Dismiss"
                >
                  <Icons.Dismiss className="tw-h-3.5 tw-w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </Alert>
    </div>
  );
}

Nudge.displayName = "Nudge";
